"use client"

import { useState, useEffect, useCallback } from "react"
import { useTranslations } from "next-intl"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { motion } from "framer-motion"
import { CreationFlowInlinePicker } from "@/components/publications/creation-flow/inline-enum-picker"
import {
  COMIC_GRADE_COMPANIES,
  COMIC_PAGE_QUALITIES,
  COMIC_RAW_CONDITIONS,
  type ComicDraft,
  type ComicGradeCompany,
  type ComicPageQuality,
  type ComicRawCondition,
} from "@kraahl/contract"
import type { StepProps } from "@/types/listing"
import {
  creationFlowBlockClass,
  creationFlowInputClass,
  creationFlowSectionLabelClass,
  creationFlowMutedClass,
} from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"

const MIN_GRADE = 0.5
const MAX_GRADE = 10

export function ComicConditionStep({
  data,
  updateDataAction,
  onValidationChange,
  onRegisterSave,
  hideButton,
  compact,
}: StepProps) {
  const t = useTranslations("comicCreation.condition")
  // Generic keys shared with the card flow (toggle description, cert hints).
  const tCard = useTranslations("cardCreation.condition")
  const draft = data.comicDraft

  const [isGraded, setIsGraded] = useState(draft?.isGraded ?? false)
  const [rawCondition, setRawCondition] = useState<ComicRawCondition | undefined>(
    draft?.rawCondition ?? undefined
  )
  const [gradeCompany, setGradeCompany] = useState<ComicGradeCompany | undefined>(
    draft?.gradeCompany ?? undefined
  )
  const [gradeValue, setGradeValue] = useState(draft?.gradeValue?.toString() ?? "")
  const [certNumber, setCertNumber] = useState(draft?.certNumber ?? "")
  const [pageQuality, setPageQuality] = useState<ComicPageQuality | undefined>(
    draft?.pageQuality ?? undefined
  )

  const parsedGrade = gradeValue.trim() === "" ? null : Number(gradeValue)
  // 0.5 .. 10.0 in half-point steps (CGC/CBCS scale).
  const gradeValid =
    parsedGrade != null &&
    Number.isFinite(parsedGrade) &&
    parsedGrade >= MIN_GRADE &&
    parsedGrade <= MAX_GRADE &&
    Number.isInteger(parsedGrade * 2)

  const isValid = isGraded
    ? Boolean(gradeCompany && gradeValid)
    : Boolean(rawCondition)

  useEffect(() => {
    onValidationChange?.(isValid)
  }, [isValid, onValidationChange])

  const saveData = useCallback(() => {
    // Graded/raw coherence at the source: the opposite path's fields are
    // explicitly nulled so the draft always passes the ComicDraftSchema
    // superRefine (graded ⇒ company+value, no rawCondition; raw ⇒ no slab
    // fields) — mirrors the DB CHECKs on listing_comic_details.
    const comicDraft: ComicDraft = {
      ...(draft ?? {}),
      isGraded,
      rawCondition: isGraded ? null : rawCondition ?? null,
      gradeCompany: isGraded ? gradeCompany ?? null : null,
      gradeValue: isGraded && gradeValid ? parsedGrade : null,
      certNumber: isGraded ? certNumber.trim() || null : null,
      pageQuality: pageQuality ?? null,
    }
    updateDataAction({ comicDraft })
  }, [
    draft, isGraded, rawCondition, gradeCompany, gradeValid, parsedGrade,
    certNumber, pageQuality, updateDataAction,
  ])

  useEffect(() => {
    onRegisterSave?.(saveData)
    return () => { onRegisterSave?.(null) }
  }, [saveData, onRegisterSave])

  const sectionLabel = creationFlowSectionLabelClass
  const blockBase = creationFlowBlockClass
  const blockPadding = compact ? "p-3" : "p-4"
  const inputBase = creationFlowInputClass
  const mutedText = creationFlowMutedClass

  // Static enum vocabularies (5 and 7 entries) sit under ENUM_MENU_MAX_ITEMS,
  // so the inline picker applies unconditionally — no SearchableDropdown
  // fallback branch like the taxonomy-fed card step needs.
  const pageQualityBlock = (
    <div className={cn("rounded-2xl", blockBase, blockPadding)}>
      <p className={cn(sectionLabel, "mb-3")}>{t("pageQuality")}</p>
      <CreationFlowInlinePicker
        items={COMIC_PAGE_QUALITIES.map((q) => ({ id: q, name: t(`pq.${q}`) }))}
        selectedId={pageQuality}
        onSelect={(id) => setPageQuality(id as ComicPageQuality)}
      />
    </div>
  )

  return (
    <div className={compact ? "space-y-4 px-1 pt-2" : "space-y-6 px-2 pt-4"}>
      <motion.div className={compact ? "space-y-4" : "space-y-5"} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>

        <div className={cn("rounded-2xl", blockBase, blockPadding)}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">{t("graded")}</p>
              <p className={cn("text-xs", mutedText)}>{tCard("gradedToggleDesc")}</p>
            </div>
            <div>
              <Switch checked={isGraded} onCheckedChange={setIsGraded} />
            </div>
          </div>
        </div>

        {!isGraded && (
          <>
            <div className={cn("rounded-2xl", blockBase, blockPadding)}>
              <p className={cn(sectionLabel, "mb-3")}>{t("rawCondition")}</p>
              <div className="grid grid-cols-3 gap-2">
                {COMIC_RAW_CONDITIONS.map((c) => {
                  const isSelected = rawCondition === c
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setRawCondition(c)}
                      className={cn(
                        "rounded-xl px-3 py-3 text-center text-sm font-semibold transition-colors",
                        isSelected
                          ? "bg-sheet-handle text-foreground ring-1 ring-foreground/30"
                          : "bg-muted/50 text-foreground/80 hover:bg-muted"
                      )}
                    >
                      {t(`raw.${c}`)}
                    </button>
                  )
                })}
              </div>
            </div>

            {pageQualityBlock}
          </>
        )}

        {isGraded && (
          <>
            <div className={cn("rounded-2xl", blockBase, blockPadding)}>
              <p className={cn(sectionLabel, "mb-3")}>{t("company")}</p>
              <CreationFlowInlinePicker
                items={COMIC_GRADE_COMPANIES.map((c) => ({ id: c, name: t(`companies.${c}`) }))}
                selectedId={gradeCompany}
                onSelect={(id) => setGradeCompany(id as ComicGradeCompany)}
                otherLast
              />
            </div>

            {gradeCompany && (
              <div className={cn("rounded-2xl", blockBase, blockPadding)}>
                <p className={cn(sectionLabel, "mb-3")}>{t("gradeValue")}</p>
                <Input
                  type="text" inputMode="decimal"
                  placeholder="9.8"
                  value={gradeValue}
                  onChange={(e) => {
                    const v = e.target.value
                    if (v === "" || /^[0-9]*\.?[0-9]?$/.test(v)) setGradeValue(v)
                  }}
                  className={inputBase}
                />
                {gradeValue.trim() !== "" && !gradeValid && (
                  <p className="text-xs text-red-400 mt-1">{MIN_GRADE}–{MAX_GRADE}</p>
                )}
              </div>
            )}

            {gradeCompany && (
              <div className={cn("rounded-2xl", blockBase, blockPadding)}>
                <p className={cn(sectionLabel, "mb-3")}>{t("certNumber")} <span className={cn("text-xs font-normal normal-case", mutedText)}>{tCard("certRecommended")}</span></p>
                <Input
                  placeholder={tCard("certPlaceholder")}
                  value={certNumber}
                  onChange={(e) => setCertNumber(e.target.value)}
                  maxLength={60}
                  className={inputBase}
                />
              </div>
            )}

            {pageQualityBlock}
          </>
        )}
      </motion.div>
    </div>
  )
}
